import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const CLOSE = 0.17;
// checks only: slows every lid motion so frame captures can sample each phase
export const slow = () => window.__afaeLidSlow || 1;
// warm: a journey's own blink (the pond): a quick close, a brief hold, a slightly slower open; the lids glow warm and let the scene show faintly through
export const WARM = { close: 0.15, hold: 0.06, open: 0.26 };
export const LOOK = {
  wall: {
    lid: "bg-[radial-gradient(ellipse_at_center,#3A2650_0%,#1E1428_70%)]",
    veil: "absolute inset-0 bg-[#2A1D3A]/30 backdrop-blur-[5px]",
  },
  warm: {
    lid: "bg-[radial-gradient(ellipse_at_center,rgba(92,48,46,0.8)_0%,rgba(46,24,30,0.84)_72%)]",
    veil: "absolute inset-0 bg-[#4A2A2A]/20 backdrop-blur-[3px]",
  },
};
// almond lids: the curve's depth follows the width (shallow on portrait phones), the upper lid sweeps lower than the lower lid rises,
// they meet along a soft line at mid-screen with a little lash shadow, and the feathered edges spill past the screen so no bright wedges show
const DEPTH = "clamp(12px, 4.5vw, 12vh)";
const BLEED = "28px";
export const LID = {
  top: { top: `-${BLEED}`, left: `-${BLEED}`, right: `-${BLEED}`, height: `calc(50% + ${BLEED} + ${DEPTH} + 6px)`, borderRadius: `0 0 50% 50% / 0 0 ${DEPTH} ${DEPTH}`, filter: "blur(3px)", boxShadow: "inset 0 -14px 16px -8px rgba(14,8,12,0.72)" },
  bottom: { bottom: `-${BLEED}`, left: `-${BLEED}`, right: `-${BLEED}`, height: `calc(50% + ${BLEED} + ${DEPTH} * 0.45)`, borderRadius: `50% 50% 0 0 / calc(${DEPTH} * 0.45) calc(${DEPTH} * 0.45) 0 0`, filter: "blur(3px)", boxShadow: "inset 0 12px 14px -8px rgba(14,8,12,0.6)" },
};

export function useBlink(onClosed) {
  const [state, setState] = useState({ closed: false, count: 0 });
  const busy = useRef(false);
  const cb = useRef(onClosed);
  cb.current = onClosed;
  const blink = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    setState((s) => ({ closed: true, count: s.count + 1 }));
    setTimeout(() => cb.current?.(), (CLOSE * 1000 + 20) * slow());
    setTimeout(() => setState((s) => ({ ...s, closed: false })), (CLOSE * 1000 + 60) * slow());
    setTimeout(() => (busy.current = false), 420 * slow());
  }, []);
  useEffect(() => {
    let id;
    const next = () => (id = setTimeout(() => (blink(), next()), 7000 + Math.random() * 5000));
    next();
    return () => clearTimeout(id);
  }, [blink]);
  return { ...state, blink };
}

export function Eyelids({ closed, count, variant = "wall", className = "fixed inset-0 z-[78]", testid = "blink-overlay" }) {
  const reduced = useReducedMotion();
  const look = LOOK[variant];
  const dur = variant === "warm" ? (closed ? WARM.close : WARM.open) : CLOSE;
  const t = { duration: dur * slow(), ease: closed ? [0.55, 0, 0.9, 0.5] : [0.1, 0.5, 0.45, 1] };
  return (
    <div data-testid={testid} data-closed={closed ? "true" : "false"} data-blinks={count} data-reduced={reduced ? "true" : "false"} className={`pointer-events-none overflow-hidden ${className}`}>
      <motion.div className={look.veil} initial={false} animate={{ opacity: closed ? 1 : 0 }} transition={reduced ? { duration: 0.45 } : t} />
      {!reduced && (
        <>
          <motion.div data-testid="eyelid-top" className={`absolute origin-top ${look.lid}`} style={LID.top} initial={false} animate={{ scaleY: closed ? 1 : 0 }} transition={t} />
          <motion.div data-testid="eyelid-bottom" className={`absolute origin-bottom ${look.lid}`} style={LID.bottom} initial={false} animate={{ scaleY: closed ? 1 : 0 }} transition={t} />
        </>
      )}
    </div>
  );
}
